/* eslint-disable camelcase */
import { Button, Grid, Card, Typography, CardContent, Stack } from '@mui/material'
import { push } from '@lagunovsky/redux-react-router'
import InstallationInfosPanel from 'installation/components/installations/components/InstallationInfosPanel'
import InstallationOuvragePanel from 'installation/components/installations/components/InstallationOuvragePanel'
import InstallationSampleCharacteristicsPanel from 'installation/components/installations/components/InstallationSampleCharacteristicsPanel'
import LocalisationMapPanel from 'installation/components/installations/components/LocalisationMapPanel'
import { DEPARTEMENT_TAG } from 'installation/constants/InstallationConstants'
import DtoInstallation from 'installation/dto/installation/DtoInstallation'
import { orderBy, max, keys } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import StationTankPanel from 'station/components/link/StationTankPanel'
import StationLocationInfoPanel from 'station/components/location/StationLocationInfoPanel'
import { getSetting, getSettingInt, isTrue } from 'utils/SettingUtils'
import { hasValue } from 'utils/NumberUtil'
import Select from '../../../components/forms/Select'
import ContactAction from '../../../referencial/components/contact/actions/ContactAction'
import ContactLightPanel from '../panels/ContactLightPanel'
import GeoApiAction from 'geoApi/action/GeoApiAction'
import { NoDataPanel } from 'components/card/NoDataCard'
import StepperDialog from 'components/modal/StepperDialog'
import { CardTitle } from 'components/card/NewCard'
import { darkBlue } from 'utils/constants/ColorTheme'

const STEP_MAP = 0
const STEP_LOCATION = 1
const STEP_DESCRIPTION = 2
const STEP_OWNER = 3
const STEP_CHARACTERISTICS = 4
const STEP_WORK = 5
const STEP_DETENTION = 6

const InstallationStepperModal = ({
    title,
    point,
    open,
    onCancel,
    onValidate,
    disabled,
}) => {
    const [newPoint, setNewPoint] = useState({
        typeName: 'installation',
        installationType: 18,
        ...point,
    })

    const {
        citiesIndex,
        contacts,
        applicationSettings,
        installationsWithGeo,
        installationAgriTanks,
    } = useSelector(store => ({
        citiesIndex: store.CityReducer.citiesIndex,
        contacts: store.ContactReducer.contacts,
        applicationSettings: store.AdministrationReducer.applicationSettings,
        installationsWithGeo: store.InstallationReducer.installationsWithGeo,
        installationAgriTanks: store.InstallationReducer.installationAgriTanks,
    }), shallowEqual)

    const [owner, setOwner] = useState(contacts.find(({ id }) => id === newPoint.ownerCode ?? {}))

    const dispatch = useDispatch()

    const maxIdTank = useMemo(() => max(installationAgriTanks.map(({ idTank }) => idTank)) + 1, [installationAgriTanks])

    const useAutomaticCodificationInstallation = isTrue(getSetting(applicationSettings, 'useAutomaticCodificationInstallation') || false)
    const defaultDepartmentCodificationInstallation = getSetting(applicationSettings, 'defaultDepartmentCodificationInstallation') || ''

    useEffect(() => {
        if (!useAutomaticCodificationInstallation || point.id) return
        const slicedTownCode = newPoint.townCode ? newPoint.townCode.slice(0, 2) : undefined
        const prefixCodificationInstallation = getSetting(applicationSettings, 'prefixCodificationInstallation') || ''
        const prefixFormatted = prefixCodificationInstallation.includes(DEPARTEMENT_TAG) ?
            prefixCodificationInstallation.replace(DEPARTEMENT_TAG, slicedTownCode ?? defaultDepartmentCodificationInstallation)
            :
            prefixCodificationInstallation

        const suffixCodificationInstallation = getSetting(applicationSettings, 'suffixCodificationInstallation') || ''
        const suffixFormatted = suffixCodificationInstallation.includes(DEPARTEMENT_TAG) ?
            suffixCodificationInstallation.replace(DEPARTEMENT_TAG, slicedTownCode ?? defaultDepartmentCodificationInstallation)
            :
            suffixCodificationInstallation

        const minLengthCodificationInstallation = parseInt(getSetting(applicationSettings, 'minLengthCodificationInstallation') || 0)

        const maxId = !installationsWithGeo?.length ? 1 : max(installationsWithGeo.map(({ code = '' }) => {
            if (code.startsWith(prefixFormatted) && code.endsWith(suffixFormatted)) {
                const str = code.replace(prefixFormatted, '') || ''
                return parseInt(str.slice(0, str.length - suffixFormatted.length)) || 0
            }
            return 0
        })) + 1
        const newId = maxId.toString().length >= minLengthCodificationInstallation ?
            maxId
            :
            `${'0'.repeat(minLengthCodificationInstallation - maxId.toString().length)}${maxId}`
        setNewPoint(prev => ({ ...prev, code: `${prefixFormatted}${newId}${suffixFormatted}` }))
    }, [newPoint.townCode])

    useEffect(() => setOwner(contacts.find(({ id }) => id === newPoint.ownerCode) ?? {}), [newPoint.ownerCode, contacts])

    const formattedPoint = useMemo(() => {
        return {
            ...newPoint,
            link_geo: newPoint.link_geo && newPoint.link_geo.map((c) => {
                return {
                    ...c,
                    idStation: newPoint.id || -1,
                }
            }),
            link_sampleCharacteristics: newPoint.link_sampleCharacteristics && newPoint.link_sampleCharacteristics.map((c) => {
                return {
                    ...c,
                    idInstallation: newPoint.id || -1,
                }
            }),
            link_sampleCasing: newPoint.link_sampleCasing && newPoint.link_sampleCasing.map((c) => {
                return {
                    ...c,
                    idInstallation: newPoint.id || -1,
                }
            }),
            link_sampleTanks: newPoint.link_sampleTanks && newPoint.link_sampleTanks.filter((t) => t.idTank || (!t.idTank && t.id && keys(t).filter((key) => t[key]).length > 1)).map((t) => {
                return {
                    ...t,
                    idTank: t.idTank || maxIdTank,
                    idInstallation: newPoint.id || -1,
                }
            }),
        }
    }, [newPoint, maxIdTank])

    const onSave = () => {
        if (owner.name) {
            if (!owner.id) {
                dispatch(ContactAction.createContact(owner, 'noRedirection')).then((ownerCode) => {
                    dispatch(ContactAction.fetchContacts()).then(() => {
                        onValidate({ ...formattedPoint, ownerCode })
                    })
                })
            } else {
                dispatch(ContactAction.updateContact(owner)).then(() => {
                    dispatch(ContactAction.fetchContacts()).then(() => {
                        onValidate(formattedPoint)
                    })
                })
            }
        } else {
            onValidate(formattedPoint)
        }
    }

    const onChangeLocation = (newGeoData = {}) => {
        setNewPoint(prev => ({
            ...prev,
            link_geo: [{
                ...prev.link_geo,
                idStation: prev.id,
                parcel: newGeoData.parcel,
                section: newGeoData.section,
            }],
            projection: newGeoData.projection,
            x: newGeoData.x,
            y: newGeoData.y,
        }))
    }

    const onChangeCity = (townCode) => {
        setNewPoint(prev => ({
            ...prev,
            townCode,
        }))
    }

    const onSelectOwner = (recievedOwner) => {
        setNewPoint(prev => ({ ...prev, ownerCode: recievedOwner.id }))
    }

    const onChangeInstallationLocation = (updatedInfo) => {
        const updatedPoint = { ...newPoint, ...updatedInfo }
        if (updatedPoint.projection === 16 && hasValue(updatedInfo.x) && hasValue(updatedInfo.y) &&
            ((point.x !== updatedInfo.x) || (point.y !== updatedInfo.y))) {
            dispatch(GeoApiAction.fetchTownsByCoordinate(updatedPoint.x, updatedPoint.y)).then((towns) => {
                const town = towns[0]
                if (town) {
                    setNewPoint({ ...updatedPoint, townCode: town.code })
                } else {
                    setNewPoint(updatedPoint)
                }
            })
        } else {
            setNewPoint(updatedPoint)
        }
    }

    const onChangeTank = (obj) => {
        const oldTank = newPoint?.link_sampleTanks?.length ? newPoint?.link_sampleTanks?.length[0] : {}
        const updateTank = {
            ...oldTank,
            ...obj,
        }
        const otherTanks = newPoint?.link_sampleTanks?.length ? newPoint?.link_sampleTanks.slice(1) : []
        setNewPoint(prev => ({
            ...prev,
            link_sampleTanks: [updateTank, ...otherTanks],
        }))
    }

    const getStep = (step) => {
        const params = {
            station: newPoint,
            onChange: (datas) => setNewPoint(prev => ({ ...prev, ...datas })),
            onChangeVisit: () => { },
            disabled,
        }
        switch (step) {
            case STEP_MAP:
                return (
                    <LocalisationMapPanel
                        installation={newPoint}
                        onChangeLocation={onChangeLocation}
                        onChangeCity={onChangeCity}
                        disabled={disabled}
                        mapHeight='450px'
                    />
                )
            case STEP_LOCATION:
                return (
                    <Card>
                        <CardContent>
                            <StationLocationInfoPanel
                                station={newPoint}
                                onChange={onChangeInstallationLocation}
                                disabled={disabled}
                                onChangeVisit={() => {}}
                            />
                        </CardContent>
                    </Card>
                )
            case STEP_DESCRIPTION:
                return (
                    <Card>
                        <CardContent>
                            <InstallationInfosPanel
                                {...params}
                                newPoint={!newPoint.id}
                                noEditCode
                            />
                        </CardContent>
                    </Card>
                )
            case STEP_OWNER:
                const contactsFormatted = contacts.map((c) => {
                    const cityFound = citiesIndex[c.cityCode]
                    return {
                        ...c,
                        labelAutocomplete: `${c.name}${cityFound ? ` - ${c.postalCode || ''} ${cityFound.name}` : ''}`,
                    }
                })
                return (
                    <Card>
                        <CardContent>
                            <Grid size={6}>
                                <Select
                                    label={i18n.owner}
                                    value={newPoint.ownerCode}
                                    options={contactsFormatted}
                                    keyValue='id'
                                    keyLabel='labelAutocomplete'
                                    onChange={(_, obj) => onSelectOwner(obj || {})}
                                    disabled={disabled}
                                    clearFunction
                                />
                            </Grid>
                            <Card>
                                <CardTitle title={owner.id ? i18n.change : i18n.create} color={darkBlue} />
                                <CardContent>
                                    <ContactLightPanel
                                        contact={owner}
                                        onChange={newOwner => setOwner(prev => ({ ...prev, ...newOwner }))}
                                        disabled={disabled}
                                    />
                                </CardContent>
                            </Card>
                        </CardContent>
                    </Card>
                )
            case STEP_CHARACTERISTICS:
                return (
                    <Card>
                        <CardContent>
                            <Grid container size={12} columnSpacing={1}>
                                <InstallationSampleCharacteristicsPanel {...params} />
                            </Grid>
                        </CardContent>
                    </Card>
                )
            case STEP_WORK:
                return (
                    <Card>
                        <CardContent>
                            <Grid container columnSpacing={1}>
                                <InstallationOuvragePanel {...params} />
                            </Grid>
                        </CardContent>
                    </Card>
                )
            case STEP_DETENTION:
                return (
                    <Card>
                        <CardContent>
                            {newPoint?.link_sampleTanks?.length ? (
                                <Stack spacing={1}>
                                    <StationTankPanel
                                        tank={newPoint?.link_sampleTanks?.length ?
                                            orderBy(newPoint.link_sampleTanks, 'idTank')[0]
                                            :
                                            {}
                                        }
                                        disabled={disabled}
                                        onChange={onChangeTank}
                                    />
                                    <Grid container justifyContent='space-between' alignItems={'center'} spacing={1}>
                                        <Typography sx={{ fontWeight: 'bold' }}>
                                            {`${i18n.nbTanks} : ${newPoint?.link_sampleTanks?.length}`}
                                        </Typography>
                                        {!disabled ? (
                                            <Button
                                                variant='contained'
                                                color='primary'
                                                onClick={() => {
                                                    setNewPoint(prev => ({
                                                        ...prev,
                                                        link_sampleTanks: orderBy(newPoint.link_sampleTanks, 'idTank').slice(1),
                                                    }))
                                                }}
                                            >
                                                {i18n.removeTank}
                                            </Button>
                                        ) : null}
                                        <Grid size={12}>
                                            <Typography sx={{ fontStyle: 'italic' }}>{i18n.onlyFirstTankShow}</Typography>
                                        </Grid>
                                    </Grid>
                                </Stack>
                            ) : (
                                <Grid container direction='column' spacing={1} alignItems='center'>
                                    <NoDataPanel />
                                    {!disabled &&
                                    <Button
                                        variant='contained'
                                        onClick={() => {
                                            const defaultTankType = getSettingInt(applicationSettings, 'defaultTankType')
                                            if (isTrue(getSetting(applicationSettings, 'useAutomaticCodificationTank') || false)) {
                                                const prefixCodificationTank = getSetting(applicationSettings, 'prefixCodificationTank') || ''
                                                const suffixCodificationTank = getSetting(applicationSettings, 'suffixCodificationTank') || ''
                                                const minLengthCodificationTank = getSettingInt(applicationSettings, 'minLengthCodificationTank') || 0
                                                const maxRefTank = !installationAgriTanks.length ? 1 : max(installationAgriTanks.map((p) => {
                                                    const str = p?.id?.replace(prefixCodificationTank, '') || ''
                                                    return parseInt(str.slice(0, str.length - suffixCodificationTank.length)) || 0
                                                })) + 1
                                                const newId = maxRefTank.toString().length >= minLengthCodificationTank ? maxRefTank : `${'0'.repeat(minLengthCodificationTank - maxRefTank.toString().length)}${maxRefTank}`
                                                onChangeTank({ id: `${prefixCodificationTank}${newId}${suffixCodificationTank}`, tankType: defaultTankType })
                                            } else {
                                                onChangeTank({ tankType: defaultTankType })
                                            }
                                        }}
                                    >{i18n.createNewTank}</Button>
                                    }
                                </Grid>
                            )}
                        </CardContent>
                    </Card>
                )
            default:
                return null
        }
    }

    const onConsult = () => {
        dispatch(push(`/station/installation/${point.id}/dashboard`))
    }

    return (
        <StepperDialog
            fullWidth
            maxWidth='lg'
            open={open}
            closeDialog={onCancel}
            title={title || i18n.updatePointPrel}
            sx={{ minHeight: '85vh', maxHeight: '85vh' }}
            bypassValidateSteps
            steps={[{
                label: i18n.map,
                constant: STEP_MAP,
            }, {
                label: i18n.localisation,
                constant: STEP_LOCATION,
            }, {
                label: i18n.descriptive,
                constant: STEP_DESCRIPTION,
            }, {
                label: i18n.owner,
                constant: STEP_OWNER,
            }, {
                label: i18n.characteristics,
                constant: STEP_CHARACTERISTICS,
            }, {
                label: i18n.work,
                constant: STEP_WORK,
            }, {
                label: i18n.detention,
                constant: STEP_DETENTION,
            }]}
            renderSaveButton={() => (
                disabled ? (
                    <Button onClick={onConsult} variant='contained' color='primary'>
                        {i18n.consult}
                    </Button>
                ) : (
                    <Button onClick={onSave} variant='contained' color='primary' disabled={!newPoint.code}>
                        {i18n.validate}
                    </Button>
                )
            )}
        >
            {step => getStep(step)}
        </StepperDialog>
    )
}

InstallationStepperModal.propTypes = {
    open: PropTypes.bool,
    disabled: PropTypes.bool,
    point: PropTypes.instanceOf(DtoInstallation),
    onCancel: PropTypes.func,
    onValidate: PropTypes.func,
    title: PropTypes.string,
}

export default InstallationStepperModal
